import { ReactNode, useEffect, useState, useMemo } from "react";

// third-party
import { IntlProvider, MessageFormatElement } from "react-intl";

// project imports
import useConfig from "hooks/useConfig";
import { I18n } from "types/config";

// load locales files
const loadLocaleData = (locale: I18n) => {
  switch (locale) {
    case "fr":
      return import("utils/locales/fr.json");
    case "ro":
      return import("utils/locales/ro.json");
    case "zh":
      return import("utils/locales/zh.json");
    case "en":
    default:
      return import("utils/locales/en.json");
  }
};

interface Props {
  children: ReactNode;
}

// ==============================|| LOCALIZATION ||============================== //

export default function Locales({ children }: Props) {
  const { i18n } = useConfig();

  const [messages, setMessages] = useState<
    Record<string, string> | Record<string, MessageFormatElement[]> | undefined
  >();
  const localeDataPromise = useMemo(() => loadLocaleData(i18n), [i18n]);

  useEffect(() => {
    localeDataPromise.then(
      (d: {
        default:
          | Record<string, string>
          | Record<string, MessageFormatElement[]>
          | undefined;
      }) => {
        setMessages(d.default);
      },
    );
  }, [localeDataPromise]);

  const handleIntlError = (error: unknown) => {
    if (
      error &&
      typeof error === "object" &&
      "code" in error &&
      (error as { code?: string }).code === "MISSING_TRANSLATION"
    ) {
      return;
    }
    // Keep non-translation i18n errors visible during development.
    console.error(error);
  };

  return (
    <>
      {messages && (
        <IntlProvider
          locale={i18n}
          defaultLocale="en"
          messages={messages}
          onError={handleIntlError}
        >
          {children}
        </IntlProvider>
      )}
    </>
  );
}
